import { useCallback, useEffect, useMemo, useState } from "react";

import { ExternalIntegrationIds } from "@remar/shared/dist/constants";

import { useElements, useStripe } from "@stripe/react-stripe-js";

import ReactPixel from "react-facebook-pixel";

import { useHistory } from "react-router-dom";
import { RootState, useAppDispatch, useAppSelector } from "store";

import { IPaymentType } from "store/features/Auth/auth.model";
import {
	changeSubscriptionType,
	getFullState,
	selectInactiveSubscription,
	setError,
	setSelectRenewalSubscription,
	setSelectSubModal,
	setUpgradeModal
} from "store/features/Auth/authSlice";
import {
	getFullState as getFullAccountState,
	renewSubscription,
	selectSubcriptionIsLoading,
	upgradeSubscription
} from "store/features/MyAccount/myAccountSlice";

import { setBannerActionLoading, setBannerData } from "store/features/notifications/notifications.slice";

import { PixelEvent, getCallbackUrl, setTiktokEvent } from "modules/utils";

const initialFormValues = {
	countryId: "",
	address1: "",
	address2: "",
	city: "",
	state: "",
	zip: "",
	phoneNumber: ""
};

interface InType {
	onSubmit: () => void;
	isRenewSubscription?: boolean;
}

interface OutType {
	closeModal;
	handleSubmit;
	handleNext;
	handleBack;
	initialFormValues;
	subcriptionIsLoading;
	error;
	activeStep;
	setActiveStep;
	paymentType;
	setPaymentType;
	initStep?: number;
}

export const useUpgradeForm = ({ onSubmit, isRenewSubscription }: InType): OutType => {
	const stripe = useStripe();
	const dispatch = useAppDispatch();
	const elements = useElements();
	const history = useHistory();

	const initStep = useMemo(() => (isRenewSubscription ? 1 : 0), [isRenewSubscription]);
	const [activeStep, setActiveStep] = useState(initStep);
	const [paymentType, setPaymentType] = useState<IPaymentType>(IPaymentType.CARD);

	const subcriptionIsLoading = useAppSelector(selectSubcriptionIsLoading);
	const { subscriptionId } = useAppSelector(selectInactiveSubscription);
	const {
		errorMessage: error,
		validatedCoupon,
		inactiveSubscription: { nextUserSubscriptionType: activeSubscription },
		paymentDiscount,
		selectSubModal
	}: RootState["auth"] = useAppSelector(getFullState);
	const { userInfo } = useAppSelector(getFullAccountState);

	const callBackUrl = useMemo(() => getCallbackUrl(history.location.search), [history.location.search]);

	useEffect(() => {
		setActiveStep(initStep);
	}, [initStep]);

	useEffect(() => {
		if (activeSubscription) {
			dispatch(changeSubscriptionType(activeSubscription?.id as number));
		}
	}, [activeSubscription, dispatch]);

	const handleNext = (values: typeof initialFormValues) => {
		switch (activeStep) {
			case 1:
				setActiveStep(2);
				break;
			case 2:
				selectSubModal ? handleSelectNextSubscription() : handleSubmit(values);
				break;
		}
	};

	const upgradeSideEffect = useCallback(
		values => {
			// hotfix: activeSubscription is undefined after qb only subscription upgrade
			if (activeSubscription) {
				const priceData = activeSubscription!.subTypeEIDItems?.find(
					({ integrationId }) => integrationId == ExternalIntegrationIds.Stripe
				)?.data;
				const { price } = priceData;
				ReactPixel.track(PixelEvent.Purchase, { ...values, value: price, currency: "USD" });
				setTiktokEvent(PixelEvent.CompletePayment, { ...values, value: price, currency: "USD" });
			}

			if (callBackUrl) {
				window.location.replace(callBackUrl);
			} else {
				onSubmit();
			}
		},
		[activeSubscription, callBackUrl, onSubmit]
	);

	const renewSideEffect = useCallback(() => {
		if (callBackUrl) {
			window.location.replace(callBackUrl);
		} else {
			onSubmit();
		}
	}, [callBackUrl, onSubmit]);

	const handleBack = () => {
		setActiveStep(prevActiveStep => (prevActiveStep >= 1 ? prevActiveStep - 1 : 0));
	};

	const handleSelectNextSubscription = () => {
		dispatch(setError(""));
		renewSideEffect();
	};

	const handleSubmit = (values: typeof initialFormValues) => {
		const couponCode = validatedCoupon && validatedCoupon.coupon.code;
		const paymentAndDiscountId = paymentDiscount?.id;
		dispatch(setError(""));
		if (isRenewSubscription) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			dispatch(
				renewSubscription({
					id: subscriptionId as number,
					subscriptionBody: {
						...values,
						zipCode: values.zip,
						userId: userInfo?.shippingDetails?.userId,
						fullName: userInfo?.shippingDetails?.fullName || userInfo!.firstName + userInfo!.lastName || ""
					},
					dispatch,
					sideEffect: renewSideEffect,
					handleError: setError,
					elements,
					stripe,
					couponCode
				})
			).then(r => {
				if (r.error) {
					dispatch(setError(r.error.message));
					setActiveStep(3);
					return;
				}
				dispatch(setBannerData({ showBanner: false })); // disappear the banner subscription renew/resume
			});
		} else {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			dispatch(
				upgradeSubscription({
					elements,
					stripe,
					values,
					sideEffect: upgradeSideEffect,
					couponCode,
					paymentAndDiscountId,
					paymentType
				})
			).then(r => {
				if (r.error) {
					dispatch(setError(r.error.message));
					setActiveStep(3);
					return;
				}
				dispatch(setBannerData({ showBanner: false })); // disappear the banner after upgrade to paid subscription
			});
		}
	};

	const closeModal = () => {
		dispatch(setUpgradeModal(false));
		dispatch(setBannerActionLoading(false));
		dispatch(setSelectRenewalSubscription(false));
		dispatch(setSelectSubModal(false));
		setActiveStep(isRenewSubscription ? 1 : 0);
	};

	return {
		closeModal,
		handleSubmit,
		handleNext,
		handleBack,
		initialFormValues,
		subcriptionIsLoading,
		error,
		activeStep,
		setActiveStep,
		initStep,
		paymentType,
		setPaymentType
	};
};
